<div class="ci-page">

  <!-- Page heading -->
  <div class="ci-heading">
    <span class="ci-kicker">Record context</span>
    <h2>{{ showNotes ? 'Notes & Custom Info' : 'Custom info' }}</h2>
    <p *ngIf="showNotes">Keep notes and organization-specific information together in two clearly separated workspaces.</p>
    <p *ngIf="!showNotes">Facility-defined information that stays with this record.</p>
  </div>

  <div class="ci-grid" [class.has-notes]="showNotes">

    <!-- ===== Notes (resident records) ===== -->
    <mat-card class="ci-card" *ngIf="showNotes">

      <div class="ci-card__head">
        <div class="ci-card__titles">
          <span class="ci-kicker">Notes</span>
          <h3>Notes</h3>
          <p>Time-stamped notes, calls, emails and meetings from your team.</p>
        </div>
        <div class="ci-card__actions">
          <span class="ci-count" *ngIf="AllNotes?.length">
            {{ AllNotes.length }} {{ AllNotes.length === 1 ? 'note' : 'notes' }}
          </span>
          <button mat-flat-button color="primary" class="ci-add" (click)="addNote()">
            <mat-icon>add</mat-icon> Add note
          </button>
        </div>
      </div>

      <div class="ci-toolbar">
        <div class="ci-search" [class.is-active]="noteSearch">
          <mat-icon>search</mat-icon>
          <input type="text" placeholder="Search notes..." [(ngModel)]="noteSearch" aria-label="Search notes" />
          <button type="button" class="ci-search__clear" *ngIf="noteSearch" (click)="noteSearch = ''"
            matTooltip="Clear search" aria-label="Clear search">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <mat-form-field class="ci-filter" appearance="outline">
          <mat-icon matPrefix>filter_list</mat-icon>
          <mat-select [(ngModel)]="noteTypeFilter" aria-label="Filter notes by type">
            <mat-option value="">All notes</mat-option>
            <mat-option *ngFor="let t of NoteTypes" [value]="t.touchName">{{ t.touchName }}</mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <div class="ci-list">
        <div class="ci-item" *ngFor="let n of filteredNotes">
          <div class="ci-note">
            <span class="ci-note__icon" [ngClass]="'is-' + typeTone(n.touchTypeName)">
              <mat-icon>{{ typeIcon(n.touchTypeName) }}</mat-icon>
            </span>

            <div class="ci-note__body">
              <div class="ci-note__title">
                <h4>{{ n.touchTypeName || 'Note' }}</h4>
                <span class="ci-chip" *ngIf="n.touchStatusName">{{ n.touchStatusName }}</span>
                <span class="ci-chip ci-chip--portal" *ngIf="n.viewOn == 1">
                  <mat-icon>{{ n.byPortal != null ? 'call_made' : 'call_received' }}</mat-icon>
                  {{ n.byPortal != null ? 'Added for portal' : 'Added from portal' }}
                </span>
              </div>

              <div class="ci-note__text touch-note">
                <p [innerHTML]="n.touchNoteNew"></p>
              </div>

              <div class="ci-note__meta">
                <span class="ci-note__avatar">{{ initials(n.createdByFirstName, n.createdByLastName) }}</span>
                <span class="ci-note__author">{{ n.createdByFirstName | titlecase }} {{ n.createdByLastName | titlecase }}</span>
                <span class="ci-note__dot">•</span>
                <span>{{ n.createdOn + 'Z' | date:'MM/dd/yyyy' }}</span>
                <ng-container *ngIf="n.startDate || n.touchDate">
                  <span class="ci-note__dot">•</span>
                  <span class="ci-note__when">
                    <mat-icon>event</mat-icon>
                    <ng-container *ngIf="n.startDate">{{ n.startDate + 'Z' | date:'MM/dd/yyyy' }}</ng-container>
                    <ng-container *ngIf="n.startDate && n.touchDate"> – </ng-container>
                    <ng-container *ngIf="n.touchDate">{{ n.touchDate + 'Z' | date:'MM/dd/yyyy' }}</ng-container>
                    <ng-container *ngIf="n.startTime || n.touchTime">
                      · {{ (n.startTime || n.touchTime) | date:'h:mm a' }}
                    </ng-container>
                  </span>
                </ng-container>
              </div>
            </div>

            <div class="ci-note__actions">
              <button mat-icon-button [matMenuTriggerFor]="noteMenu" matTooltip="More" aria-label="Note options">
                <mat-icon>more_horiz</mat-icon>
              </button>
              <mat-menu #noteMenu="matMenu" xPosition="before">
                <button mat-menu-item (click)="editNote(n)"><mat-icon>edit</mat-icon> Edit</button>
                <button mat-menu-item (click)="deleteNote(n)"><mat-icon>delete_outline</mat-icon> Delete</button>
              </mat-menu>
            </div>
          </div>
        </div>

        <div class="ci-empty" *ngIf="!notesLoaded">
          <p>Loading notes…</p>
        </div>

        <div class="ci-empty" *ngIf="notesLoaded && AllNotes.length == 0">
          <mat-icon>sticky_note_2</mat-icon>
          <p>No notes added yet.</p>
        </div>

        <div class="ci-empty" *ngIf="AllNotes.length && filteredNotes.length == 0">
          <mat-icon>search_off</mat-icon>
          <p>No notes match these filters.</p>
        </div>
      </div>
    </mat-card>

    <!-- ===== Custom info ===== -->
    <mat-card class="ci-card">

      <div class="ci-card__head">
        <div class="ci-card__titles">
          <span class="ci-kicker">Custom info</span>
          <h3>Custom info</h3>
          <p>Flexible details created for your organization's needs.</p>
        </div>
        <div class="ci-card__actions">
          <span class="ci-count" *ngIf="AllCustomInfoList?.length">
            {{ AllCustomInfoList.length }} {{ AllCustomInfoList.length === 1 ? 'item' : 'items' }}
          </span>
          <button mat-flat-button color="primary" class="ci-add" (click)="addCustomInfo()">
            <mat-icon>add</mat-icon> Add custom info
          </button>
        </div>
      </div>

      <!-- Toolbar: search (client side) + type filter (server side, unchanged) -->
      <div class="ci-toolbar">
        <div class="ci-search" [class.is-active]="searchText">
          <mat-icon>search</mat-icon>
          <input type="text" placeholder="Search custom info..." [(ngModel)]="searchText" aria-label="Search custom info" />
          <button type="button" class="ci-search__clear" *ngIf="searchText" (click)="searchText = ''"
            matTooltip="Clear search" aria-label="Clear search">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <mat-form-field class="ci-filter" *ngIf="AllCustomInfoListShow" appearance="outline">
          <mat-icon matPrefix>filter_list</mat-icon>
          <mat-select matInput placeholder="All custom info" (selectionChange)="onChangeSelectedType($event)">
            <mat-option *ngFor="let rel of CustomInfoTypeForFilter" [value]="rel.customInfoTypeID">
              {{rel.customInfoName}}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <div class="ci-list">
        <ng-container *ngFor="let touch of AllCustomInfoList; let i = index">
          <div class="ci-item" *ngIf="matchesSearch(touch)">
            <app-customInfo-list [touch]="touch" (editCustomInfoEmmitter)="editCustomInfo($event)"
              (deleteCustomInfoEmmitter)="deleteCustomInfo($event)">
            </app-customInfo-list>
          </div>
        </ng-container>

        <div class="ci-empty" *ngIf="AllCustomInfoList == null && AllCustomInfoListShow">
          <mat-icon>sticky_note_2</mat-icon>
          <p>No custom info added yet.</p>
        </div>

        <div class="ci-empty" *ngIf="AllCustomInfoList?.length && searchText && !hasSearchMatches()">
          <mat-icon>search_off</mat-icon>
          <p>No custom info matches "{{ searchText }}".</p>
        </div>
      </div>

    </mat-card>

  </div>

</div>
